// notifyOnWarrantyClaim — fires on warranty claim opened, photo added, or
// status change. Mirrors the notifyChadOnBuildApproval pattern (Apr 29).
//
// Recipients are read from finance_settings.warranty_writers (value_jsonb,
// JSON array of emails). Adding/removing reviewers = one row update, no code
// deploy. Original RM is also notified on open + status change for awareness.
//
// Channel matrix:
//   open:           writers → in-portal + Team-line SMS · RM → in-portal only
//   photo_added:    writers → in-portal only (would be too noisy years out)
//   status_changed: writers → in-portal only · RM → in-portal only
//
// Fire-and-forget: any failure is logged but never blocks the caller.

import { supabase } from "./supabase";

const PORTAL_BASE_URL = "https://app.bardinroofing.com";

interface DealForNotify {
  id: string;
  homeowner_name: string | null;
  property_street: string | null;
  property_city: string | null;
  owner_id: string | null;
}

interface ClaimSummary {
  id: string;
  title: string | null;
  category: string | null;
  status: string | null;
  opened_via: string | null;
}

function toE164(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

function buildAddressLine(deal: DealForNotify): string {
  const parts = [deal.property_street, deal.property_city].filter(Boolean) as string[];
  return parts.join(", ");
}

/** Resolve writer team_roster rows from the finance_settings allowlist. */
async function getWriterRows(): Promise<Array<{ id: string; full_name: string; phone: string | null }>> {
  try {
    const { data: settingRow } = await supabase
      .from("finance_settings")
      .select("value_jsonb")
      .eq("key", "warranty_writers")
      .maybeSingle();
    const emails = Array.isArray(settingRow?.value_jsonb) ? (settingRow!.value_jsonb as string[]) : [];
    if (emails.length === 0) return [];
    const { data: rows } = await supabase
      .from("team_roster")
      .select("id, full_name, phone")
      .in("email", emails)
      .eq("is_active", true);
    return rows ?? [];
  } catch (err) {
    console.warn("[notifyOnWarrantyClaim] getWriterRows failed:", err);
    return [];
  }
}

async function postNotification(payload: {
  user_id: string;
  type: string;
  title: string;
  body?: string | null;
  deal_id?: string | null;
  metadata?: Record<string, unknown>;
}) {
  const { error } = await supabase.from("notifications").insert(payload);
  if (error) console.warn("[notifyOnWarrantyClaim] notification insert failed:", error.message);
}

async function sendSms(phone: string, body: string, tag: string) {
  try {
    await supabase.functions.invoke("twilio-send-sms", {
      body: {
        to: phone,
        body,
        // Intentionally no deal_id passed — staff-internal alert, not a homeowner
        // conversation. Matches notifyChadOnBuildApproval pattern.
        tag,
      },
    });
  } catch (err) {
    console.warn("[notifyOnWarrantyClaim] SMS send failed:", err);
  }
}

/** Fired when a new warranty_claims row is inserted (either path). */
export async function notifyWarrantyClaimOpened(deal: DealForNotify, claim: ClaimSummary): Promise<void> {
  try {
    const homeowner = deal.homeowner_name || "(no name)";
    const addr = buildAddressLine(deal);
    const dealUrl = `${PORTAL_BASE_URL}/deal/${deal.id}?tab=warranty`;
    const titleSummary = claim.title ? ` — ${claim.title}` : "";
    const inPortalTitle = `Warranty claim opened — ${homeowner}${titleSummary}`;
    const inPortalBody = addr || dealUrl;
    const smsBody = `Warranty claim opened: ${homeowner}${addr ? ` (${addr})` : ""}${titleSummary}. Tap: ${dealUrl}`;

    const writers = await getWriterRows();
    const tasks: Promise<unknown>[] = [];

    for (const w of writers) {
      tasks.push(
        postNotification({
          user_id: w.id,
          type: "warranty_claim_opened",
          title: inPortalTitle,
          body: inPortalBody,
          deal_id: deal.id,
          metadata: { url: dealUrl, claim_id: claim.id, opened_via: claim.opened_via, category: claim.category },
        }),
      );
      const phoneE164 = toE164(w.phone);
      if (phoneE164) tasks.push(sendSms(phoneE164, smsBody, "warranty_claim_opened"));
    }

    // Original RM gets in-portal awareness only — no SMS, would be noisy years
    // out from sale. Skip when the RM is also a writer (already notified above).
    const writerIds = new Set(writers.map((w) => w.id));
    if (deal.owner_id && !writerIds.has(deal.owner_id)) {
      tasks.push(
        postNotification({
          user_id: deal.owner_id,
          type: "warranty_claim_opened",
          title: `Warranty claim opened on your deal — ${homeowner}${titleSummary}`,
          body: "Jennifer is handling this. You'll see status updates here for awareness.",
          deal_id: deal.id,
          metadata: { url: dealUrl, claim_id: claim.id, awareness: true },
        }),
      );
    }

    const results = await Promise.allSettled(tasks);
    for (const r of results) {
      if (r.status === "rejected") console.warn("[notifyOnWarrantyClaim:opened] task failed:", r.reason);
    }
  } catch (err) {
    console.warn("[notifyOnWarrantyClaim:opened] unexpected error:", err);
  }
}

/** Fired when a deal_documents row with warranty_claim_id is inserted. */
export async function notifyWarrantyPhotoAdded(
  deal: DealForNotify,
  claim: ClaimSummary,
  photoCount: number,
  /** e.g. "2 photos and 1 file" — defaults to "N photo(s)". */
  countLabel?: string,
): Promise<void> {
  try {
    const homeowner = deal.homeowner_name || "(no name)";
    const dealUrl = `${PORTAL_BASE_URL}/deal/${deal.id}?tab=warranty`;
    const titleSummary = claim.title ? ` — ${claim.title}` : "";
    const addedLabel = countLabel || `${photoCount} ${photoCount === 1 ? "photo" : "photos"}`;
    const writers = await getWriterRows();
    const tasks: Promise<unknown>[] = [];

    for (const w of writers) {
      tasks.push(
        postNotification({
          user_id: w.id,
          type: "warranty_photo_added",
          title: `${addedLabel} added to warranty claim — ${homeowner}${titleSummary}`,
          body: dealUrl,
          deal_id: deal.id,
          metadata: { url: dealUrl, claim_id: claim.id, photo_count: photoCount },
        }),
      );
    }

    const results = await Promise.allSettled(tasks);
    for (const r of results) {
      if (r.status === "rejected") console.warn("[notifyOnWarrantyClaim:photo] task failed:", r.reason);
    }
  } catch (err) {
    console.warn("[notifyOnWarrantyClaim:photo] unexpected error:", err);
  }
}

/** Fired when warranty_claims.status changes (terminal or non-terminal). */
export async function notifyWarrantyStatusChanged(
  deal: DealForNotify,
  claim: ClaimSummary,
  fromStatus: string,
  toStatus: string,
): Promise<void> {
  try {
    const homeowner = deal.homeowner_name || "(no name)";
    const dealUrl = `${PORTAL_BASE_URL}/deal/${deal.id}?tab=warranty`;
    const titleSummary = claim.title ? ` — ${claim.title}` : "";
    const statusLabel = toStatus.replace(/_/g, " ");
    const inPortalTitle = `Warranty claim ${statusLabel}${titleSummary}`;
    const inPortalBody = `${homeowner} · was ${fromStatus.replace(/_/g, " ")}`;

    const writers = await getWriterRows();
    const writerIds = new Set(writers.map((w) => w.id));
    const tasks: Promise<unknown>[] = [];

    for (const w of writers) {
      tasks.push(
        postNotification({
          user_id: w.id,
          type: "warranty_status_changed",
          title: inPortalTitle,
          body: inPortalBody,
          deal_id: deal.id,
          metadata: { url: dealUrl, claim_id: claim.id, from: fromStatus, to: toStatus },
        }),
      );
    }

    if (deal.owner_id && !writerIds.has(deal.owner_id)) {
      tasks.push(
        postNotification({
          user_id: deal.owner_id,
          type: "warranty_status_changed",
          title: `Warranty claim on your deal — ${statusLabel}`,
          body: `${homeowner}${titleSummary}`,
          deal_id: deal.id,
          metadata: { url: dealUrl, claim_id: claim.id, from: fromStatus, to: toStatus, awareness: true },
        }),
      );
    }

    const results = await Promise.allSettled(tasks);
    for (const r of results) {
      if (r.status === "rejected") console.warn("[notifyOnWarrantyClaim:status] task failed:", r.reason);
    }
  } catch (err) {
    console.warn("[notifyOnWarrantyClaim:status] unexpected error:", err);
  }
}
